import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">
            文章未找到
          </h1>
          <Link
            to="/"
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '太子千秋万载文化研究',
    },
    url: `https://yanxiya.cn/article/${article.slug}`,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://yanxiya.cn/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.category,
        item: `https://yanxiya.cn/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 太子千秋万载`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://yanxiya.cn/article/${article.slug}`}
        schemaJson={articleSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="w-full px-4 md:px-8">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  {
                    name: article.category,
                    path: `/category/${article.categorySlug}`,
                  },
                  { name: article.title },
                ]}
              />
            </div>
          </div>

          <div className="w-full px-4 md:px-8 py-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-10">
                <article className="flex-1 min-w-0">
                  <div className="relative w-full h-64 md:h-96 rounded-lg overflow-hidden bg-background-100 mb-8">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>

                  <div className="flex items-center gap-2 mb-4">
                    <Link
                      to={`/category/${article.categorySlug}`}
                      className="px-3 py-1 text-xs font-medium rounded-full bg-primary-50 text-primary-600 hover:bg-primary-100 transition-colors whitespace-nowrap"
                    >
                      {article.category}
                    </Link>
                  </div>

                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                    {article.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-6 border-b border-background-200">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-user-line"></i>
                      {article.author}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-calendar-line"></i>
                      {article.date}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-time-line"></i>
                      {article.readTime}
                    </span>
                  </div>

                  <div
                    className="prose prose-lg max-w-none
                      prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                      prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                      prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4 prose-p:text-sm md:prose-p:text-base
                      prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700
                      prose-strong:text-foreground-900"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />

                  <div className="mt-10 pt-6 border-t border-background-200">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-foreground-500 mr-2">标签：</span>
                      {article.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </article>

                <aside className="w-full lg:w-80 flex-shrink-0">
                  <div className="sticky top-24">
                    <div className="bg-background-100 rounded-lg border border-background-200/70 p-6 mb-6">
                      <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-4">
                        作者信息
                      </h3>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
                          <i className="ri-user-line text-primary-600"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-800">
                            {article.author}
                          </p>
                          <p className="text-xs text-foreground-500">文化研究者</p>
                        </div>
                      </div>
                    </div>

                    {relatedArticles.length > 0 && (
                      <div className="bg-background-100 rounded-lg border border-background-200/70 p-6">
                        <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-4">
                          相关文章
                        </h3>
                        <div className="space-y-4">
                          {relatedArticles.map((ra) => (
                            <Link
                              key={ra.id}
                              to={`/article/${ra.slug}`}
                              className="block group"
                            >
                              <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
                                {ra.title}
                              </h4>
                              <span className="text-xs text-foreground-400">
                                {ra.date} · {ra.readTime}
                              </span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                    <Link
                      to={`/category/${article.categorySlug}`}
                      className="block mt-4 px-4 py-3 text-sm text-center font-medium rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors border border-background-200/70 whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line mr-1.5"></i>
                      返回{article.category}分类
                    </Link>
                  </div>
                </aside>
              </div>
            </div>
          </div>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(breadcrumbSchema),
            }}
          />
        </main>

        <Footer />
      </div>
    </>
  );
}